import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import {
  TrendingUp,
  DollarSign,
  ShoppingBag,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Download,
  Store,
} from 'lucide-react';
import { format, subDays } from 'date-fns';

interface RevenueStats {
  totalPlatformRevenue: number;
  revenueChange: number;
  totalSales: number;
  salesChange: number;
  averageOrderValue: number;
  totalOrders: number;
  ordersChange: number;
  topStores: Array<{
    storeId: string;
    storeName: string;
    totalSales: number;
    platformFees: number;
    orderCount: number;
  }>;
}

interface RevenueData {
  date: string;
  platformFees: number;
  totalSales: number;
  orderCount: number;
}

export default function Revenue() {
  const [timeRange, setTimeRange] = useState<string>('month');
  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  // Fetch revenue statistics
  const { data: stats, isLoading: statsLoading } = useQuery<RevenueStats>({
    queryKey: ['revenue-stats', timeRange],
    queryFn: async () => {
      return apiClient.get<RevenueStats>(`/revenue/stats?range=${timeRange}`);
    },
  });

  // Fetch revenue timeline data
  const { data: revenueData, isLoading: dataLoading } = useQuery<RevenueData[]>({
    queryKey: ['revenue-data', startDate, endDate],
    queryFn: async () => {
      return apiClient.get<RevenueData[]>(`/revenue/timeline?startDate=${startDate}&endDate=${endDate}`);
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const renderTrend = (change: number) => {
    const isPositive = change >= 0;
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    const colorClass = isPositive ? 'text-black' : 'text-gray-400';

    return (
      <div className="flex items-center gap-2 mt-3">
        <Icon className={`w-4 h-4 ${colorClass}`} />
        <span className={`text-sm font-medium ${colorClass}`}>
          {Math.abs(change).toFixed(1)}% from last period
        </span>
      </div>
    );
  };

  const calculateTotal = (data: RevenueData[] | undefined, key: keyof RevenueData) => {
    if (!data) return 0;
    return data.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-black">Platform Revenue</h1>
          <p className="text-gray-500 mt-2">Track 6% platform fees from all sales</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="h-12 px-4 rounded-xl border-2 border-gray-200 bg-white focus:border-black focus:outline-none transition-colors font-medium"
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
          </select>
          <button className="h-12 px-6 rounded-xl bg-black text-white font-bold hover:bg-gray-800 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Platform Revenue (6% fees) */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Platform Revenue</h3>
            {statsLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatCurrency(stats?.totalPlatformRevenue || 0)}</p>
                {renderTrend(stats?.revenueChange || 0)}
              </>
            )}
          </div>
        </div>

        {/* Total Sales (100%) */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Sales</h3>
            {statsLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatCurrency(stats?.totalSales || 0)}</p>
                {renderTrend(stats?.salesChange || 0)}
              </>
            )}
          </div>
        </div>

        {/* Fee Percentage */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Percent className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Platform Fee</h3>
            <p className="text-3xl font-black text-black mt-2">6.0%</p>
            <p className="text-sm text-gray-500 mt-3">Of every sale</p>
          </div>
        </div>

        {/* Total Orders */}
        <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="absolute top-6 right-6">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-16">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Orders</h3>
            {statsLoading ? (
              <div className="h-12 bg-gray-100 rounded-lg mt-2 animate-pulse"></div>
            ) : (
              <>
                <p className="text-3xl font-black text-black mt-2">{formatNumber(stats?.totalOrders || 0)}</p>
                {renderTrend(stats?.ordersChange || 0)}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Over Time */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black text-black">Revenue Timeline</h2>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 px-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10 px-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
              />
            </div>
          </div>

          {dataLoading ? (
            <div className="h-64 bg-gray-100 rounded-lg animate-pulse"></div>
          ) : revenueData && revenueData.length > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase">Total Fees</p>
                  <p className="text-xl font-black text-black mt-1">
                    {formatCurrency(calculateTotal(revenueData, 'platformFees'))}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase">Total Sales</p>
                  <p className="text-xl font-black text-black mt-1">
                    {formatCurrency(calculateTotal(revenueData, 'totalSales'))}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase">Orders</p>
                  <p className="text-xl font-black text-black mt-1">
                    {formatNumber(calculateTotal(revenueData, 'orderCount'))}
                  </p>
                </div>
              </div>

              {/* Simple list view */}
              <div className="max-h-96 overflow-y-auto space-y-2">
                {revenueData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">
                        {format(new Date(item.date), 'MMM d, yyyy')}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Platform Fee</p>
                        <p className="text-sm font-bold text-black">{formatCurrency(item.platformFees)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Sales</p>
                        <p className="text-sm font-medium text-gray-700">{formatCurrency(item.totalSales)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Orders</p>
                        <p className="text-sm font-medium text-gray-700">{item.orderCount}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No revenue data for selected period
            </div>
          )}
        </div>

        {/* Top Earning Stores */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black text-black">Top Revenue Stores</h2>
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              By Platform Fees
            </span>
          </div>

          {statsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse"></div>
              ))}
            </div>
          ) : stats?.topStores && stats.topStores.length > 0 ? (
            <div className="space-y-4">
              {stats.topStores.map((store, index) => (
                <div key={store.storeId} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <p className="text-sm font-bold text-black truncate">{store.storeName}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{store.orderCount} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-black">{formatCurrency(store.platformFees)}</p>
                    <p className="text-xs text-gray-500">{formatCurrency(store.totalSales)} sales</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No store data available
            </div>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl p-8 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Percent className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-black mb-2">Platform Fee Structure</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              The platform charges a 6% fee on all completed sales. This fee is automatically calculated and deducted from merchant revenues.
              Fees are collected on orders with status: PROCESSING, SHIPPED, or DELIVERED. Cancelled or refunded orders are excluded.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
